import { defineStore } from 'pinia';
import CompanyNotesAPI from 'dashboard/api/companyNotes';
import { throwErrorMessage } from 'dashboard/store/utils/api';
import camelcaseKeys from 'camelcase-keys';

export const useCompanyNotesStore = defineStore('companyNotes', {
  state: () => ({
    records: [],
    uiFlags: {
      fetchingList: false,
      creating: false,
      updating: false,
      deleting: false,
    },
  }),

  getters: {
    getNotes: state => {
      if (!state.records || !Array.isArray(state.records)) return [];
      return camelcaseKeys([...state.records], { deep: true });
    },
    getUIFlags: state => state.uiFlags,
  },

  actions: {
    setUIFlag(flags) {
      this.uiFlags = { ...this.uiFlags, ...flags };
    },

    async get(companyId) {
      this.setUIFlag({ fetchingList: true });
      try {
        const { data } = await CompanyNotesAPI.get(companyId);
        this.records = data || [];
        return data || [];
      } catch (error) {
        throwErrorMessage(error);
        this.records = [];
        return [];
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async create(companyId, content) {
      this.setUIFlag({ creating: true });
      try {
        const { data } = await CompanyNotesAPI.create(companyId, content);
        if (data) {
          this.records.unshift(data);
        }
        return data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ creating: false });
      }
    },

    async update(companyId, noteId, content) {
      this.setUIFlag({ updating: true });
      try {
        const { data } = await CompanyNotesAPI.update(companyId, noteId, content);
        const index = this.records.findIndex(note => note.id === noteId);
        if (index !== -1) {
          this.records[index] = data;
        }
        return data;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ updating: false });
      }
    },

    async delete(companyId, noteId) {
      this.setUIFlag({ deleting: true });
      try {
        await CompanyNotesAPI.delete(companyId, noteId);
        this.records = this.records.filter(note => note.id !== noteId);
        return true;
      } catch (error) {
        throwErrorMessage(error);
        throw error;
      } finally {
        this.setUIFlag({ deleting: false });
      }
    },

    clearRecords() {
      this.records = [];
    },
  },
});
